/* =========================================================================
   EzyRefurb COD popup — own implementation (replaces PenguinCOD's storefront JS)
   -------------------------------------------------------------------------
   WHAT THIS FIXES vs PenguinCOD (see ~/ezy-ops/cod app issues/):
   1) NO "clearStale" wipe handler. PenguinCOD deleted the payment-type marker on
      pageshow/visibilitychange/popstate, racing its own checkout write with no lock
      → customer sometimes charged FULL. We never clear intent in lifecycle handlers,
      and all cart writes go through ONE serialized queue with a redirect latch.
   2) NO hardcoded deposit-% fallback. PenguinCOD hardcoded 10% and only corrected to
      the real 5% after a slow fetch — if the fetch lost the race the customer was
      quoted 10%. Here the percentage/cap are rendered SERVER-SIDE into the page by
      Liquid (window.EZYCOD_CONFIG), so there is no fetch and no race. If config is
      missing we FAIL SAFE (disable the partial option) rather than guess.
   3) ₹50,000 COD CAP enforced (R2) — the rule PenguinCOD never implemented:
         advance = MAX(percentOf(total), total − codCap)
         cod     = total − advance         (always ≤ ₹50,000)
   4) COUPON GATING (R5): a coupon must never apply to a COD order — but it must also never
      take the choice away. Both options stay selectable; if a code is present the COD option
      says so, the button reads "Remove <CODE> & continue", and pressing it clears the code
      before checkout. Enforced again server-side (the deposit discount is non-combinable).

   NOTE: the money is ultimately computed and enforced SERVER-SIDE. This script is the
   UI + intent capture; it must never be the source of truth for what is charged.
   ========================================================================= */
(function () {
  'use strict';

  var CFG = window.EZYCOD_CONFIG;
  var LOG = function (m) { if (CFG && CFG.debug) console.log('[ezycod]', m); };

  /* ---------------------------------------------------------------- money -- */
  // All math in integer paise. Mirrors core/advance.js exactly (single rule set).
  function computeSplit(totalPaise) {
    var base;
    if (CFG.mode === 'fixed') base = Math.round(CFG.fixedPaise || 0);
    else base = Math.round((totalPaise * (CFG.percent || 0)) / 100);
    // COD ceiling disabled 26 Sep 2026: a null/absent cap means NO cap, so capFloor is 0 and the
    // advance is purely the percentage. `|| 0` would have been a disaster here — it would make
    // capFloor the whole cart and charge the customer 100% up front.
    var cap = CFG.codCapPaise;
    var capFloor = (cap === null || cap === undefined || cap === '')
      ? 0
      : Math.max(0, totalPaise - cap);
    var advance = Math.max(base, capFloor);
    if (advance > totalPaise) advance = totalPaise;
    return { advance: advance, cod: totalPaise - advance, capApplied: capFloor > base };
  }

  function money(paise) {
    var r = paise / 100;
    // Indian digit grouping (1,23,456.78) — matches the store's Rs. format
    var s = r.toFixed(2);
    var parts = s.split('.');
    var i = parts[0];
    var last3 = i.slice(-3), rest = i.slice(0, -3);
    var grouped = rest ? rest.replace(/\B(?=(\d{2})+(?!\d))/g, ',') + ',' + last3 : last3;
    return 'Rs. ' + grouped + (parts[1] === '00' ? '' : '.' + parts[1]);
  }

  /* ------------------------------------------------------- cart write queue -- */
  // Every cart mutation is serialized here. `redirecting` latches TRUE the moment we
  // begin the checkout hand-off and is never unset — so nothing can clobber the
  // intent we just wrote (this is the structural fix for PenguinCOD's bug #1).
  var redirecting = false;
  var queue = Promise.resolve();
  function enqueue(fn) {
    queue = queue.then(fn, fn);
    return queue;
  }
  function postJSON(url, body, timeoutMs) {
    // A cart write with NO timeout is what let "Setting up…" hang indefinitely: if
    // /cart/update.js never answers, the promise never settles and the UI is stuck forever.
    var ctl = typeof AbortController === 'function' ? new AbortController() : null;
    var timer = ctl ? setTimeout(function () { ctl.abort(); }, timeoutMs || 12000) : null;
    var done = function (v) { if (timer) clearTimeout(timer); return v; };
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify(body),
      signal: ctl ? ctl.signal : undefined
    }).then(function (r) { done(); return r; }, function (e) { done(); throw e; })
      .then(function (r) {
      if (!r.ok) return r.text().then(function (t) { throw new Error(url + ' → ' + r.status + ' ' + t.slice(0, 200)); });
      return r.json();
    });
  }
  function getCart(timeoutMs) {
    var ctl = typeof AbortController === 'function' ? new AbortController() : null;
    var timer = ctl && timeoutMs ? setTimeout(function () { ctl.abort(); }, timeoutMs) : null;
    return fetch('/cart.js', {
      headers: { 'Accept': 'application/json' },
      signal: ctl ? ctl.signal : undefined
    }).then(function (r) {
      if (timer) clearTimeout(timer);
      if (!r.ok) throw new Error('/cart.js → ' + r.status);
      return r.json();
    }, function (e) { if (timer) clearTimeout(timer); throw e; });
  }

  /* ------------------------------------------------------------- prefetch -- */
  // PERFORMANCE: the popup must show real amounts immediately, not after a round-trip.
  // Previously it waited for a POST (clear stale marker) THEN a GET (/cart.js) before any
  // number could render — on the preview theme that left "Cart Total: -" on screen for 30-40s.
  // We now warm the cart in the background as soon as the page is idle, so opening the popup
  // renders from memory. This is display-only: the amount actually charged is computed
  // server-side by the Discount Function, and Proceed re-reads the cart at the last moment.
  var cartCache = null, cartCacheAt = 0, cartInFlight = null;
  var CACHE_MS = 30000;

  /**
   * Fetch the cart, retrying transient failures.
   *
   * WHY RETRIES (bug found 2026-08-09): the first version of this used a single 8s abort.
   * On the unpublished preview theme — which has no warm cache and can be genuinely slow —
   * that abort fired routinely, surfacing "Could not load your cart" and leaving the shopper
   * unable to pick COD. Storefront requests can also be briefly throttled (HTTP 429).
   * A slow cart must degrade into "wait a moment", never into a dead end.
   */
  function getCartWithRetry(attempts) {
    var tries = attempts || 3;
    var delays = [400, 1200, 2500];
    function attempt(i) {
      return getCart(12000).catch(function (e) {
        if (i >= tries - 1) throw e;
        return new Promise(function (res) { setTimeout(res, delays[i] || 2500); }).then(function () {
          return attempt(i + 1);
        });
      });
    }
    return attempt(0);
  }

  function warmCart() {
    if (cartInFlight) return cartInFlight;
    cartInFlight = getCartWithRetry(3).then(function (c) {
      cartCache = c; cartCacheAt = Date.now(); cartInFlight = null; return c;
    }, function (e) { cartInFlight = null; throw e; });
    return cartInFlight;
  }

  function cartFresh() {
    return cartCache && (Date.now() - cartCacheAt) < CACHE_MS ? cartCache : null;
  }

  function whenIdle(fn) {
    if (typeof requestIdleCallback === 'function') requestIdleCallback(fn, { timeout: 2000 });
    else setTimeout(fn, 300);
  }
  // Warm on load, and refresh whenever the cart changes (theme fires these on add/update).
  whenIdle(function () { warmCart().catch(function () {}); });
  ['cart:updated', 'cart:refresh', 'cart:change'].forEach(function (ev) {
    document.addEventListener(ev, function () { cartCache = null; warmCart().catch(function () {}); });
  });

  /**
   * Build a provisional cart from THIS PAGE's own product data — no network at all.
   *
   * Shopify publishes `window.ShopifyAnalytics.meta.product.variants[] = {id, price}` with
   * price in paise, and the add-to-cart form carries the selected variant id and quantity.
   *
   * WHY THIS IS SAFE (and does NOT reintroduce the A->B race): this data belongs to the page
   * the customer is currently standing on. On Product B's page it physically cannot contain
   * Product A's price. It is not a cache and never outlives the page.
   *
   * Used to paint the correct amount INSTANTLY on Buy Now, instead of leaving the fields
   * blank for the ~3 network round-trips (clear + add + fetch) the cart rebuild needs.
   * The authoritative cart still follows and reconciles.
   *
   * @returns a cart-shaped object, or null if the page doesn't expose variant data.
   */
  function provisionalCartFromPage(form) {
    try {
      var meta = window.ShopifyAnalytics && window.ShopifyAnalytics.meta;
      var variants = meta && meta.product && meta.product.variants;
      if (!variants || !variants.length || !form) return null;

      var idEl = form.querySelector('[name="id"]');
      var variantId = idEl && idEl.value;
      if (!variantId) return null;

      var match = null;
      for (var i = 0; i < variants.length; i++) {
        if (String(variants[i].id) === String(variantId)) { match = variants[i]; break; }
      }
      if (!match || typeof match.price !== 'number' || match.price <= 0) return null;

      var qEl = form.querySelector('[name="quantity"]');
      var qty = qEl && parseInt(qEl.value, 10) > 0 ? parseInt(qEl.value, 10) : 1;
      var subtotal = match.price * qty;

      return {
        __provisional: true,
        item_count: qty,
        items_subtotal_price: subtotal,
        original_total_price: subtotal,
        total_price: subtotal,
        attributes: {},
        discount_codes: []      // Buy Now starts a clean cart, so no codes apply
      };
    } catch (e) { return null; }
  }

  /**
   * The UNDISCOUNTED subtotal, in paise — the ONLY correct base for the split.
   *
   * BUG THIS FIXES (found in testing, 2026-08-09): `cart.total_price` is the total AFTER
   * cart-level discounts, which now includes OUR OWN deposit discount. Reading it created a
   * feedback loop — once the partial marker was set, the cart collapsed to the 5% advance and
   * the popup then showed 5% OF THAT (a ₹47,999 laptop displayed ₹2,399.95 as the "total" and
   * offered ₹120 now / ₹2,279.95 COD).
   *
   * `items_subtotal_price` is the sum of line items after line-item discounts but BEFORE
   * cart-level discounts, so it is immune to our own discount — and it matches exactly what
   * the server-side Function uses (`cart.cost.subtotalAmount`, documented as "before taxes and
   * cart-level discounts"). That keeps the popup and the charge in lockstep.
   */
  function baseSubtotal(cart) {
    var v = cart.items_subtotal_price;
    if (typeof v === 'number' && v > 0) return v;
    v = cart.original_total_price;            // fallback: before any discount
    if (typeof v === 'number' && v > 0) return v;
    return cart.total_price;                  // last resort
  }

  /* ------------------------------------------------------------------ state -- */
  // `gen` is the state-isolation token. It increments on every popup open and every close.
  // EVERY async continuation captures the gen it started under and aborts if it no longer
  // matches — so a slow request belonging to Product A can never paint over Product B.
  // This is what makes "old response overwrites new product" structurally impossible;
  // it is not a delay or a guess.
  var state = { cart: null, cartConfirmed: false, zeroValue: false, selected: null, busy: false, mounted: false, gen: 0 };
  function newGen() { return ++state.gen; }
  function stale(g) { return g !== state.gen; }
  var el = {};

  /**
   * The customer-entered discount code on the cart, or null.
   *
   * Verified against the live cart API: `discount_codes` lists ONLY codes the customer
   * entered (e.g. [{code:"EZY500", applicable:true}]); our own automatic deposit discount
   * appears solely under `cart_level_discount_applications`. So our discount can never be
   * mistaken for a coupon here.
   */
  function couponCode(cart) {
    var list = (cart && cart.discount_codes) || [];
    if (!Array.isArray(list)) return null;
    for (var i = 0; i < list.length; i++) {
      if (list[i] && list[i].code && list[i].applicable !== false) return list[i].code;
    }
    return null;
  }

  /* ------------------------------------------------------------------- view -- */
  var TPL = [
    '<div class="ezycod-wrap">',
    '  <div class="ezycod-modal" role="dialog" aria-modal="true" aria-labelledby="ezycodTitle" tabindex="-1">',
    '    <div class="ezycod-header">',
    '      <div class="ezycod-header-row">',
    '        <div class="ezycod-header-left">',
    '          <div class="ezycod-header-left-top"><span>&#128722;</span> <span data-t="orderSummaryLabel"></span></div>',
    '          <div class="ezycod-header-items" id="ezycodItems">-</div>',
    '        </div>',
    '        <div class="ezycod-header-right">',
    '          <div class="ezycod-header-total" id="ezycodTotal">-</div>',
    '          <div class="ezycod-header-total-label" data-t="cartTotalLabel"></div>',
    '        </div>',
    '      </div>',
    '    </div>',
    '    <div class="ezycod-screen-title">',
    '      <h2 class="ezycod-title" id="ezycodTitle" data-t="popupTitle"></h2>',
    '      <p class="ezycod-subtitle" data-t="popupSubtitle"></p>',
    '    </div>',
    '    <div class="ezycod-body">',
    '      <div class="ezycod-error" id="ezycodError"></div>',
    '      <div class="ezycod-option" id="ezycodOptFull" data-option="full" role="radio" aria-checked="false" tabindex="0">',
    '        <div class="ezycod-radio"><div class="ezycod-radio-inner"></div></div>',
    '        <div class="ezycod-option-content">',
    '          <div class="ezycod-option-title" data-t="fullPaymentTitle"></div>',
    '          <div class="ezycod-option-desc" data-t="fullPaymentSubtitle"></div>',
    '        </div>',
    '        <div class="ezycod-option-amount"><div class="ezycod-amount-primary" id="ezycodFullAmt">-</div></div>',
    '      </div>',
    '      <div class="ezycod-option" id="ezycodOptPartial" data-option="partial" role="radio" aria-checked="false" tabindex="0">',
    '        <div class="ezycod-radio"><div class="ezycod-radio-inner"></div></div>',
    '        <div class="ezycod-option-content">',
    '          <div class="ezycod-option-title" data-t="partialPaymentTitle"></div>',
    '          <div class="ezycod-option-desc" data-t="partialPaymentSubtitle"></div>',
    '          <div class="ezycod-note" id="ezycodCapNote" style="display:none"></div>',
    '          <button type="button" class="ezycod-unavailable" id="ezycodPartialUnavail" style="display:none" aria-expanded="false"></button>',
    '          <div class="ezycod-unavailable-detail" id="ezycodPartialUnavailDetail"></div>',
    '        </div>',
    '        <div class="ezycod-option-amount">',
    '          <div class="ezycod-amount-label" data-t="partialDepositLabel"></div>',
    '          <div class="ezycod-amount-primary" id="ezycodNowAmt">-</div>',
    '          <div class="ezycod-amount-secondary" id="ezycodCodAmt">-</div>',
    '        </div>',
    '      </div>',
    '    </div>',
    '    <div class="ezycod-footer">',
    '      <button class="ezycod-btn" id="ezycodProceed" disabled data-t="proceedButtonText"></button>',
    '      <button class="ezycod-cancel" id="ezycodCancel" data-t="cancelButtonText"></button>',
    '    </div>',
    '  </div>',
    '</div>'
  ].join('\n');

  function mount() {
    if (state.mounted) return;
    var dlg = document.createElement('dialog');
    dlg.className = 'ezycod-dialog';
    dlg.id = 'ezycodDialog';
    dlg.innerHTML = TPL;
    document.body.appendChild(dlg);

    // fill all static labels from server-rendered config
    dlg.querySelectorAll('[data-t]').forEach(function (n) {
      var k = n.getAttribute('data-t');
      n.textContent = (CFG.text && CFG.text[k]) || '';
    });

    el.dlg = dlg;
    el.items = dlg.querySelector('#ezycodItems');
    el.total = dlg.querySelector('#ezycodTotal');
    el.fullAmt = dlg.querySelector('#ezycodFullAmt');
    el.nowAmt = dlg.querySelector('#ezycodNowAmt');
    el.codAmt = dlg.querySelector('#ezycodCodAmt');
    el.capNote = dlg.querySelector('#ezycodCapNote');
    el.unavail = dlg.querySelector('#ezycodPartialUnavail');
    el.unavailDetail = dlg.querySelector('#ezycodPartialUnavailDetail');
    el.optFull = dlg.querySelector('#ezycodOptFull');
    el.optPartial = dlg.querySelector('#ezycodOptPartial');
    el.proceed = dlg.querySelector('#ezycodProceed');
    el.cancel = dlg.querySelector('#ezycodCancel');
    el.error = dlg.querySelector('#ezycodError');

    [el.optFull, el.optPartial].forEach(function (o) {
      o.addEventListener('click', function () { select(o.dataset.option); });
      o.addEventListener('keydown', function (e) {
        if (e.key === ' ' || e.key === 'Enter') { e.preventDefault(); select(o.dataset.option); }
      });
    });
    // Tap the chip to reveal the full reason; don't let it select the option underneath.
    el.unavail.addEventListener('click', function (e) {
      e.stopPropagation();
      var open = el.unavailDetail.classList.toggle('ezycod-show');
      el.unavail.setAttribute('aria-expanded', String(open));
    });
    el.proceed.addEventListener('click', onProceed);
    el.cancel.addEventListener('click', close);
    dlg.addEventListener('cancel', function (e) { e.preventDefault(); close(); }); // ESC
    dlg.addEventListener('click', function (e) { if (e.target === dlg) close(); }); // backdrop

    state.mounted = true;
  }

  function showError(msg) {
    state.retryShown = false;
    el.error.textContent = msg;
    el.error.classList.add('ezycod-show');
  }
  function clearError() {
    state.retryShown = false;
    el.error.textContent = '';
    el.error.classList.remove('ezycod-show');
  }

  function select(opt) {
    if (state.busy) return;
    // Only a real business rule may block a choice (coupon applied / COD switched off).
    // A slow or failed cart load must NEVER make an option unselectable.
    if (opt === 'partial' && el.optPartial.classList.contains('ezycod-disabled')) return;
    state.selected = opt;
    el.optFull.classList.toggle('ezycod-selected', opt === 'full');
    el.optPartial.classList.toggle('ezycod-selected', opt === 'partial');
    el.optFull.setAttribute('aria-checked', String(opt === 'full'));
    el.optPartial.setAttribute('aria-checked', String(opt === 'partial'));
    // Continue requires a CONFIRMED cart — not merely a displayed number.
    // On Buy Now the amount is painted instantly from the page, but the cart itself is still
    // being rebuilt (clear + add). Proceeding before that lands could send the customer to
    // checkout with the PREVIOUS product still in the cart. The figure is visible immediately;
    // the button unlocks the moment the real cart confirms it (normally well under 2s).
    el.proceed.disabled = !state.cartConfirmed || state.zeroValue === true;
    syncCapNote();
    syncProceedLabel();
    if (!state.retryShown) clearError();
  }

  function render() {
    var cart = state.cart;
    var total = baseSubtotal(cart); // undiscounted subtotal — never cart.total_price (see baseSubtotal)
    var count = cart.item_count;

    // ZERO-VALUE GUARD (order #2050, 2026-08-11).
    // A cart can contain line items yet be worth nothing — e.g. a Rs 0 helper product. The
    // old code only checked `item_count`, so a Rs 0 cart rendered "Rs 0 now / Rs 0 on
    // delivery" and let the customer Continue, producing a Rs 0 order with no payment.
    // There is no meaningful advance on a Rs 0 cart, so refuse outright.
    if (!(total > 0)) {
      state.zeroValue = true;
      el.total.textContent = money(0);
      el.items.textContent = count + ' ' + ((CFG.text && CFG.text.cartItemsLabel) || 'items in cart');
      el.fullAmt.textContent = '—';
      el.nowAmt.textContent = '—';
      el.codAmt.textContent = '';
      el.proceed.disabled = true;
      showError('This cart has no payable amount. Please add a product before checking out.');
      return;
    }
    state.zeroValue = false;
    el.items.textContent = count + ' ' + ((CFG.text && CFG.text.cartItemsLabel) || 'items in cart');
    el.total.textContent = money(total);
    el.fullAmt.textContent = money(total);

    var split = computeSplit(total);
    el.nowAmt.textContent = money(split.advance);
    // Spell the second half out — "Rs X COD" was ambiguous about who pays what, when.
    el.codAmt.innerHTML = '';
    var pill = document.createElement('span');
    pill.className = 'ezycod-cod-line';
    pill.textContent = 'then ' + money(split.cod) + ' on delivery';
    el.codAmt.appendChild(pill);

    // R2 — explain when the ₹50,000 courier cap raises the advance.
    // Only meaningful for the COD option, so it is shown only while that option is selected
    // (it made no sense sitting on screen under Full Payment).
    // The "COD is limited to ₹50,000" warning was removed on 26 Sep 2026 along with the cap.
    // capApplied is false whenever there is no cap, so the note simply never shows; the text is
    // still built when a cap IS configured, so re-enabling the cap restores the message too.
    state.capApplied = split.capApplied;
    state.capText = split.capApplied
      ? 'Cash on delivery is limited to ' + money(CFG.codCapPaise) +
        ', so the balance is collected online now.'
      : '';
    syncCapNote();

    // R5 — a coupon must never apply to a COD order.
    // IMPORTANT (Gurmeet, 2026-08-09): this is enforced WITHOUT taking the choice away.
    // A discount code no longer disables COD; instead we warn, and if the customer picks COD
    // we remove the code for them (with the button spelling out exactly what will happen).
    // The ONLY thing that may disable COD is the merchant kill-switch.
    state.couponCode = couponCode(cart);
    var codOff = !CFG.codEnabled;
    el.optPartial.classList.toggle('ezycod-disabled', codOff);
    el.optPartial.setAttribute('aria-disabled', String(codOff));

    if (codOff) {
      showNotice('Cash on delivery unavailable', 'Cash on delivery is currently switched off for this store.');
    } else if (state.couponCode) {
      // Short chip; the full explanation is one tap away rather than shouting on screen.
      showNotice(state.couponCode + ' won\u2019t apply',
                 'Discount codes can\u2019t be combined with Cash on Delivery. Choosing this option removes ' +
                 state.couponCode + ' and charges the full price, split into the advance and the COD balance.');
    } else {
      hideNotice();
    }
    syncProceedLabel();
  }

  /** Cap explanation. With the COD ceiling disabled this never shows (capApplied is always false). */
  function syncCapNote() {
    var show = state.capApplied && !!state.capText && state.selected === 'partial';
    el.capNote.style.display = show ? '' : 'none';
    if (show) el.capNote.textContent = state.capText;
  }

  /** Compact amber chip + tap-to-expand detail. Info, not an error. */
  function showNotice(short, detail) {
    el.unavail.style.display = '';
    el.unavail.innerHTML = '';
    var i = document.createElement('span');
    i.className = 'ezycod-i';
    i.textContent = 'i';
    var t = document.createElement('span');
    t.textContent = short;
    el.unavail.appendChild(i);
    el.unavail.appendChild(t);
    el.unavailDetail.textContent = detail;
    el.unavailDetail.classList.remove('ezycod-show');
  }
  function hideNotice() {
    el.unavail.style.display = 'none';
    el.unavailDetail.classList.remove('ezycod-show');
  }

  /** Make the CTA state exactly what pressing it will do. */
  function syncProceedLabel() {
    if (state.busy) return;
    // Always a plain "Continue". The customer has already been told the coupon can't be used
    // with COD (see the chip on the option), so the removal happens quietly on continue
    // rather than turning the CTA into a wall of text.
    el.proceed.textContent = (CFG.text && CFG.text.proceedButtonText) || 'Continue';
  }

  function open() {
    mount();
    // FAIL SAFE: without server-rendered config we must not guess a deposit amount.
    if (!CFG || typeof CFG.percent !== 'number' && CFG.mode !== 'fixed') {
      showError('Payment options are unavailable right now. Please try again.');
      return;
    }
    // FRESH STATE FOR THIS PRODUCT. New generation invalidates every in-flight request
    // from the previous product, and every remembered value is wiped before anything paints.
    newGen();
    state.cart = null;
    state.cartConfirmed = false;
    state.zeroValue = false;
    state.selected = null;
    state.couponCode = null;
    state.capApplied = false;
    state.busy = false;
    redirecting = false;
    cartCache = null;          // the cached cart belongs to the PREVIOUS product — discard it
    cartInFlight = null;
    clearError();
    el.proceed.disabled = true;
    el.optFull.classList.remove('ezycod-selected');
    el.optPartial.classList.remove('ezycod-selected');

    if (typeof el.dlg.showModal === 'function') el.dlg.showModal();
    else { el.dlg.setAttribute('data-fallback', '1'); el.dlg.classList.add('ezycod-open'); }
    document.documentElement.style.overflow = 'hidden';

    // Paint instantly from the prefetch if we have it, then ALWAYS reconcile below.
    // Safe now (unlike the original bug): this cache is created fresh on each page load,
    // nulled on Back/bfcache restore, and nulled before every Buy-Now cart mutation — so it
    // can only ever hold THIS page's cart. Continue stays locked until reconciliation.
    var warm = cartFresh();
    if (warm && warm.item_count) {
      state.cart = warm;
      state.cartConfirmed = false;
      render();
      select('full');
    }

    // ALWAYS load fresh and reconcile. We never treat cached data as final.
    //
    // WHY (bug 2026-08-11): the old "instant path" rendered `cartFresh()` — anything under
    // 30s old — with no re-validation. Opening Product B within 30s of Product A therefore
    // painted A's price, and on the Buy-Now path the cart genuinely still held A until the
    // clear+add finished, so the customer saw A's amount for 2-3 seconds before it corrected.
    // Requirement: never display a previous product's number, not even for one second.
    // The prefetch is retained purely to make this fetch fast, never as the displayed value.
    loadCartIntoPopup();

    // 2) BACKGROUND, NON-BLOCKING — clear any stale partial marker.
    // The marker makes the server-side discount reduce the cart to the advance. If a shopper
    // picks COD, abandons checkout and returns, a lingering marker would leave the cart
    // discounted and could let an express path (Shop Pay / dynamic buttons that bypass this
    // popup) charge only the advance without them choosing COD this time. So we always reset
    // it — but OFF the critical path, because the displayed split is computed from the
    // UNDISCOUNTED subtotal and is therefore unaffected by whether the marker is set.
    enqueue(function () {
      return postJSON('/cart/update.js', { attributes: { _ezycod_payment_type: '' } })
        .then(function (c) {
          cartCache = c; cartCacheAt = Date.now();
          state.markerCleared = true;    // lets Full Payment redirect with zero round-trips
        })
        .catch(function () { state.markerCleared = false; });
    });
  }

  /**
   * Open the modal in a pure loading state with a fresh generation and NO amounts.
   * Used by Buy Now, where the cart must be rebuilt before any number is meaningful.
   */
  function openLoadingOnly() {
    newGen();
    state.cart = null; state.cartConfirmed = false; state.zeroValue = false; state.selected = null; state.couponCode = null;
    state.capApplied = false; state.busy = false; redirecting = false;
    cartCache = null; cartInFlight = null;
    clearError();
    el.optFull.classList.remove('ezycod-selected');
    el.optPartial.classList.remove('ezycod-selected');
    if (typeof el.dlg.showModal === 'function') { if (!el.dlg.open) el.dlg.showModal(); }
    else { el.dlg.setAttribute('data-fallback', '1'); el.dlg.classList.add('ezycod-open'); }
    document.documentElement.style.overflow = 'hidden';
    showLoading();
  }

  /** Show a clear loading state on the amount fields (never leave a bare "-"). */
  function showLoading() {
    el.total.textContent = '…';
    el.items.textContent = 'loading your cart…';
    el.fullAmt.textContent = '…';
    el.nowAmt.textContent = '…';
    el.codAmt.textContent = '…';
    el.proceed.disabled = true;
  }

  /**
   * Load the cart into the popup, with retries and a graceful, RECOVERABLE failure state.
   *
   * Rule (Gurmeet, 2026-08-09): a temporary cart-loading problem must NEVER disable or hide
   * the Partial Prepaid + COD option. Both choices stay visible and clickable at all times;
   * we simply can't show the amounts until the cart arrives, and Proceed waits for them.
   */
  function loadCartIntoPopup() {
    var g = state.gen;                 // this load belongs to THIS product only
    // Only blank the amounts if nothing trustworthy is on screen yet. If a provisional
    // (current-page) figure is already shown, keep it visible and reconcile underneath —
    // never flash the customer back to an empty state.
    if (!state.cart) showLoading();
    clearError();
    var stillOpen = function () {
      return (el.dlg.open || el.dlg.classList.contains('ezycod-open')) && !stale(g);
    };
    return (cartInFlight || warmCart()).then(function (cart) {
      if (!stillOpen()) return;        // customer moved on — discard this response entirely
      state.cart = cart;
      state.cartConfirmed = true;
      if (!cart.item_count) { showError('Your cart is empty.'); return; }
      clearError();
      render();
      select(state.selected || 'full');   // re-evaluates the Proceed button now data exists
    }).catch(function (e) {
      LOG(e);
      if (!stillOpen()) return;
      // Recoverable: offer a retry, keep BOTH options on screen and selectable.
      el.total.textContent = '—';
      el.items.textContent = 'cart unavailable';
      el.fullAmt.textContent = '—';
      el.nowAmt.textContent = '—';
      el.codAmt.textContent = '—';
      showRetry('Taking longer than usual to load your cart.');
    });
  }

  /** Error banner with a working "Try again" button (no page refresh needed). */
  function showRetry(msg) {
    el.error.innerHTML = '';
    var span = document.createElement('span');
    span.textContent = msg + ' ';
    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'ezycod-retry';
    btn.textContent = 'Try again';
    btn.addEventListener('click', function () {
      cartCache = null; cartInFlight = null;
      loadCartIntoPopup();
    });
    el.error.appendChild(span);
    el.error.appendChild(btn);
    el.error.classList.add('ezycod-show');
    state.retryShown = true;
  }

  function close() {
    // Previously this bailed out while busy, so a stuck "Setting up…" trapped the customer
    // with no way back. Closing now ALWAYS works and cancels whatever is in flight:
    // bumping the generation makes every pending continuation a no-op.
    newGen();
    state.busy = false;
    redirecting = false;          // allow the popup to be opened again
    state.cart = null;            // never carry a cart into the next product
    if (typeof el.dlg.close === 'function' && el.dlg.open) el.dlg.close();
    el.dlg.classList.remove('ezycod-open');
    document.documentElement.style.overflow = '';
  }

  function busy(on, label) {
    state.busy = on;
    el.proceed.disabled = on;
    el.cancel.style.visibility = on ? 'hidden' : '';
    if (on) {
      el.proceed.innerHTML = '<span class="ezycod-spinner"></span>' + (label || 'Please wait…');
    } else {
      syncProceedLabel();
    }
  }

  /* --------------------------------------------------------------- proceed -- */
  function onProceed() {
    if (state.busy || !state.selected) return;
    // Belt-and-braces: never hand a zero-value cart to checkout, on EITHER path.
    if (state.zeroValue === true || !state.cart || !(baseSubtotal(state.cart) > 0)) {
      showError('This cart has no payable amount. Please add a product before checking out.');
      return;
    }
    clearError();

    if (state.selected === 'full') {
      // Path A: untouched native Shopify checkout (coupons allowed here).
      busy(true, 'Redirecting…');
      redirecting = true;
      // SPEED: opening the popup already clears the marker in the background. If that
      // succeeded and nothing set it since, there is nothing to do — go straight to
      // checkout. We only pay for a round-trip when the state is genuinely unknown.
      // This must stay CONFIRMED-before-redirect in the unknown case: a leftover marker
      // would make the server-side discount fire on a FULL-payment order = under-charge.
      if (state.markerCleared) { window.location.href = '/checkout'; return; }
      var gf = state.gen;
      enqueue(function () {
        return postJSON('/cart/update.js', { attributes: { _ezycod_payment_type: '' } })
          .catch(function () { /* best-effort; checkout charges the full total anyway */ });
      }).then(function () {
        if (stale(gf)) return;
        window.location.href = '/checkout';
      });
      return;
    }

    // Path B: partial / COD.
    state.markerCleared = false;
    busy(true, 'Setting up…');
    redirecting = true; // latch BEFORE any write — nothing may undo the intent after this
    var g = state.gen;
    // WATCHDOG: even with per-request timeouts, never let this state persist. If the whole
    // hand-off has not completed in 20s, release the UI and let the customer retry or leave.
    var watchdog = setTimeout(function () {
      if (stale(g)) return;
      redirecting = false;
      busy(false);
      showRetry('That took too long.');
    }, 20000);
    var clearWatchdog = function () { clearTimeout(watchdog); };
    enqueue(function () {
      // R5: a coupon must never ride along on a COD order. Rather than blocking the choice,
      // we remove the code here — the button already told the customer this would happen.
      // Verified against the live cart API: POST /cart/update.js {discount:""} clears codes.
      if (!state.couponCode) return null;
      return postJSON('/cart/update.js', { discount: '' }).then(function (c) {
        if (couponCode(c)) {
          // Removal didn't stick — abort rather than risk a coupon on a COD order.
          throw new Error('coupon still applied after removal');
        }
        state.couponCode = null;
      });
    }).then(function () {
      // SPEED: one round-trip, then redirect.
      // We no longer re-read the cart first. That re-read existed to avoid writing a stale
      // split — but these attributes are ADVISORY ONLY: they record what the customer was
      // shown. The amount actually charged is recomputed server-side by the Discount Function
      // from Shopify's authoritative cart at checkout, so a slightly stale advisory number
      // cannot affect the money. The one attribute that matters is `_ezycod_payment_type`,
      // and it is written here and confirmed before we navigate.
      var shown = state.cart ? computeSplit(baseSubtotal(state.cart)) : null;
      return postJSON('/cart/update.js', {
        attributes: {
          _ezycod_payment_type: 'partial',
          _ezycod_shown_advance: shown ? String(shown.advance) : '',
          _ezycod_shown_cod: shown ? String(shown.cod) : '',
          _ezycod_shown_total: state.cart ? String(baseSubtotal(state.cart)) : '',
          _ezycod_v: CFG.version || '1'
        }
      });
    }).then(function () {
      clearWatchdog();
      if (stale(g)) return;            // popup was closed / another product opened — do not navigate
      // Hand off to the server, which RE-COMPUTES the split from authoritative Shopify
      // prices (never trusting these attributes) and drives the 5%-at-checkout flow.
      window.location.href = CFG.partialCheckoutUrl || '/checkout';
    }).catch(function (e) {
      clearWatchdog();
      if (stale(g)) return;
      LOG(e);
      redirecting = false;
      busy(false);
      if (String(e && e.message).indexOf('coupon still applied') !== -1) {
        showError('Could not remove the discount code automatically. Please remove it on the cart page, then choose Cash on Delivery.');
      } else {
        showError('Could not start Cash on Delivery. Please try again.');
      }
    });
  }

  /* ------------------------------------------------------------- intercept -- */
  // Intercept the store's checkout entry points so the popup appears first — same UX as today.
  var CHECKOUT_SEL = 'button[name="checkout"], input[name="checkout"], a[href="/checkout"], ' +
                     'a[href^="/checkout?"], .cart__checkout, #checkout, [data-ezycod-open]';
  // "Buy it now" (dynamic checkout) skips the cart entirely — must be handled separately.
  var BUYNOW_SEL = '.shopify-payment-button__button, [data-testid="Chkt-BuyNowButton"]';

  function findTrigger(node) {
    if (!node || !node.closest) return null;
    var n = node.closest(CHECKOUT_SEL);
    if (n) return { el: n, buyNow: false };
    n = node.closest(BUYNOW_SEL);
    if (n) return { el: n, buyNow: true };
    return null;
  }

  // For Buy-it-now we must put the product in the cart ourselves before showing the popup,
  // because the dynamic button would otherwise create its own checkout we can't annotate.
  function addBuyNowItemToCart(triggerEl) {
    var form = triggerEl.closest('form[action*="/cart/add"]');
    if (!form) return Promise.reject(new Error('no product form for Buy Now'));
    var idInput = form.querySelector('[name="id"]');
    var variantId = idInput && idInput.value;
    if (!variantId) return Promise.reject(new Error('no variant id'));
    var qtyInput = form.querySelector('[name="quantity"]');
    var qty = qtyInput && parseInt(qtyInput.value, 10) > 0 ? parseInt(qtyInput.value, 10) : 1;
    // Buy Now semantics = just this item, so start from a clean cart (mirrors the current app).
    return postJSON('/cart/clear.js', {}).then(function () {
      return postJSON('/cart/add.js', { items: [{ id: parseInt(variantId, 10), quantity: qty }] });
    });
  }

  document.addEventListener('click', function (e) {
    if (redirecting) return;                       // hand-off in flight — never re-enter
    if (!CFG || CFG.enabled === false) return;
    var t = findTrigger(e.target);
    if (!t) return;
    e.preventDefault();
    e.stopPropagation();

    if (!t.buyNow) { open(); return; }

    // BUY NOW — seed the cart BEFORE any amount is rendered.
    //
    // The previous code called open() first and only then cleared+added the item, so for the
    // two round-trips that took, the popup displayed whatever was already in the cart — the
    // PREVIOUS product. That was the 2-3 second stale price. Now the popup opens in a pure
    // loading state and the first number it ever paints is this product's.
    mount();
    openLoadingOnly();
    var g = state.gen;

    // INSTANT PAINT from this page's own variant data — no network, no blank fields.
    // Cannot be a previous product's price: it comes from the page we are standing on.
    var form = t.el.closest('form[action*="/cart/add"]');
    var provisional = provisionalCartFromPage(form);
    if (provisional) {
      state.cart = provisional;
      state.cartConfirmed = false;      // Continue stays locked until the real cart lands
      clearError();
      render();
      select('full');
    }

    enqueue(function () { return addBuyNowItemToCart(t.el); })
      .then(function () {
        if (stale(g)) return null;                 // customer already moved to another product
        cartCache = null; cartInFlight = null;
        return warmCart();
      })
      .then(function (cart) {
        if (stale(g) || !cart) return;             // discard a late response from a past product
        // RECONCILE: the authoritative cart replaces the provisional figure. Normally
        // identical (Buy Now clears the cart and adds exactly this variant), so the customer
        // sees no change at all — but if it differs, the real number wins.
        state.cart = cart;
        state.cartConfirmed = true;
        if (!cart.item_count) { showError('Your cart is empty.'); return; }
        clearError();
        render();
        select(state.selected || 'full');
      })
      .catch(function (err) {
        if (stale(g)) return;
        LOG(err);
        state.cartConfirmed = false;
        showRetry('Could not load this item.');
      });
  }, true); // capture phase — run before the theme's own handlers

  // On Back/forward restore, release the local re-entrancy latch and invalidate any
  // in-flight work from before the navigation. Otherwise `redirecting` stays true after a
  // successful hand-off and EVERY later Buy Now / Checkout click is silently ignored —
  // which is what made navigation feel "stuck".
  //
  // IMPORTANT: this resets ONLY our own client-side flags. It does NOT clear the cart's
  // payment marker, so it is NOT the PenguinCOD failure mode (their bug was wiping
  // `_cod_payment_type` on these very events, mid-checkout).
  window.addEventListener('pageshow', function (e) {
    if (e.persisted) { newGen(); redirecting = false; state.busy = false; cartCache = null; }
  });

  window.EzyCOD = { open: open, close: close, computeSplit: computeSplit,
                    _provisionalCartFromPage: provisionalCartFromPage };
  LOG('ready');
})();
